// src/pages/ProductDetail.js

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getScent, getScents, BEST_SELLER_HANDLE, LOW_STOCK, NEW_RELEASE_HANDLES } from '../data/scents';
import { useCart, GIFT_BAG_HANDLE } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { ShoppingBag, Check, ChevronLeft, ChevronRight, Truck, RefreshCw, Shield, Heart, Droplet } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import ProductReviews from '../components/ProductReviews';
import ProductRatingBadge from '../components/ProductRatingBadge';
import { trackViewItem } from '../lib/analytics';
import { recordView } from '../lib/recentlyViewed';
import RecentlyViewed from '../components/RecentlyViewed';
import StickyAddToCart from '../components/StickyAddToCart';

const ProductDetail = () => {
    const { handle } = useParams();
    const navigate = useNavigate();
    const { addToCart, cart, loading: cartLoading, goToCheckout } = useCart();
    const { country, symbol } = useCurrency();

    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const { isWishlisted, toggleWishlist } = useWishlist();
    const [wishlistTooltip, setWishlistTooltip] = useState(false);
    const [added, setAdded] = useState(false);
    const [addPending, setAddPending] = useState(false);
    const [buyNowPending, setBuyNowPending] = useState(false);
    const cartBeforeRef = useRef(null);
    const sliderRef = useRef(null);
    const buttonsRef = useRef(null);

    // Set page title and description from product data once loaded
    // Uses the SEO title/description from Shopify admin when set, matching
    // what netlify/edge-functions/page-meta.js puts in the page's HTML.
    usePageTitle(
        product?.seoTitle || product?.name || null,
        product?.seoDescription || product?.description || null,
        undefined,
        { rawTitle: !!product?.seoTitle }
    );

    useEffect(() => {
        setLoading(true);
        setSelectedImage(0);
        Promise.all([getScent(handle, country), getScents(country)])
            .then(([prod, all]) => {
                // The gift bag has no product page — it's added from the cart
                if (!prod || prod.handle === GIFT_BAG_HANDLE) { navigate('/scents'); return; }
                setProduct(prod);
                setRelated(all.filter(p => p.handle !== handle).slice(0, 6));
            })
            .catch(() => navigate('/scents'))
            .finally(() => setLoading(false));
    }, [handle, country, navigate]);

    // One view_item per product visited (not again when only the currency changes)
    const viewedHandleRef = useRef(null);
    useEffect(() => {
        if (!product || viewedHandleRef.current === product.handle) return;
        viewedHandleRef.current = product.handle;
        trackViewItem(product);
        recordView(product.handle);
    }, [product]);

    // Only show the "Added to Cart" confirmation once addToCart's mutation has
    // actually landed — detected by the cart object changing identity, since
    // CartContext only calls setCart on a successful add, never on failure.
    useEffect(() => {
        if (!addPending || cartLoading) return;
        setAddPending(false);
        if (cart !== cartBeforeRef.current) {
            setAdded(true);
            setTimeout(() => setAdded(false), 2000);
        }
    }, [addPending, cartLoading, cart]);

    const handleAddToCart = () => {
        cartBeforeRef.current = cart;
        setAddPending(true);
        addToCart(product.variantId);
    };

    const handleWishlist = () => {
        if (!product) return;
        const saved = toggleWishlist(product.handle);
        if (!saved) {
            setWishlistTooltip(true);
            setTimeout(() => setWishlistTooltip(false), 2000);
        }
    };

    // Only redirect to checkout once addToCart's mutation has actually landed —
    // detected by the cart object changing identity, since CartContext only
    // calls setCart on a successful add, never on failure. A failed add leaves
    // cart unchanged, so redirecting on its (stale) checkoutUrl would send the
    // shopper to checkout without the item they just tried to buy.
    useEffect(() => {
        if (!buyNowPending || cartLoading) return;
        setBuyNowPending(false);
        if (cart !== cartBeforeRef.current && cart?.checkoutUrl) {
            goToCheckout(cart);
        }
    }, [buyNowPending, cartLoading, cart, goToCheckout]);

    const handleBuyNow = () => {
        cartBeforeRef.current = cart;
        setBuyNowPending(true);
        addToCart(product.variantId, 1);
    };

    const scrollLeft = () => sliderRef.current?.scrollBy({ left: -280, behavior: 'smooth' });
    const scrollRight = () => sliderRef.current?.scrollBy({ left: 280, behavior: 'smooth' });

    const cleanNotes = (notes) => {
        if (!notes) return null;
        return notes.replace(/[\[\]"]/g, '').trim();
    };

    if (loading) return (
        <div className="bg-background min-h-screen pt-32 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground tracking-wide">Loading...</div>
        </div>
    );

    if (!product) return null;

    return (
        <div className="bg-background min-h-screen pt-24 pb-24 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">

                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
                    <Link to="/scents" className="hover:text-[#CEAC8F] transition-colors">Our Scents</Link>
                    <span>/</span>
                    <span className="text-foreground">{product.name}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">

                    {/* Product images */}
                    <div className="space-y-4">
                        <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-card">
                            {(NEW_RELEASE_HANDLES.includes(product.handle) || product.handle === BEST_SELLER_HANDLE || LOW_STOCK[product.handle]) && (
                                <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                                    {NEW_RELEASE_HANDLES.includes(product.handle) && (
                                        <span className="bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                            New
                                        </span>
                                    )}
                                    {product.handle === BEST_SELLER_HANDLE && (
                                        <span className="bg-[#CEAC8F] text-black text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                            Best Seller
                                        </span>
                                    )}
                                    {LOW_STOCK[product.handle] && (
                                        <span className="bg-red-600 text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                            Low Stock
                                        </span>
                                    )}
                                </div>
                            )}
                            <img
                                src={product.images[selectedImage]?.url || product.image}
                                alt={product.images[selectedImage]?.altText || product.name}
                                className="w-full h-full object-contain"
                            />
                        </div>
                        {product.images.length > 1 && (
                            <div className="flex gap-3">
                                {product.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSelectedImage(i)}
                                        className={`w-20 rounded-lg overflow-hidden border-2 transition-all ${
                                            selectedImage === i ? 'border-[#CEAC8F]' : 'border-border'
                                        }`}
                                    >
                                        <div className="aspect-[3/4]">
                                            <img src={img.url} alt={img.altText} className="w-full h-full object-contain" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product info */}
                    <div className="flex flex-col">
                        {product.tags?.length > 0 && (
                            <p className="text-[#CEAC8F]/70 text-xs tracking-widest mb-3 uppercase">{product.tags[0]}</p>
                        )}
                        <h1 className="text-4xl md:text-5xl font-light text-foreground mb-2 tracking-wide">
                            {product.name}
                        </h1>

                        {product.handle !== GIFT_BAG_HANDLE && (
                            <p className="text-muted-foreground/70 text-xs tracking-widest mb-4 uppercase">12ml</p>
                        )}

                        <p className={`text-3xl text-[#CEAC8F] font-light ${LOW_STOCK[product.handle] ? 'mb-2' : 'mb-6'}`}>
                            {symbol}{product.price.toFixed(2)}
                        </p>

                        {LOW_STOCK[product.handle] && (
                            <p className="text-red-600 text-sm font-medium mb-4">
                                Only {LOW_STOCK[product.handle]} left in stock
                            </p>
                        )}

                        {/* Perfume oil / 12ml explainer — answers the "why so small"
                            question right where it comes up, same copy for every product. */}
                        {product.handle !== GIFT_BAG_HANDLE && (
                            <div className="flex items-start gap-3 text-sm text-muted-foreground bg-card rounded-2xl p-6 border border-border mb-4">
                                <Droplet className="w-5 h-5 text-[#CEAC8F] flex-shrink-0 mt-0.5" />
                                <p className="leading-relaxed">
                                    <span className="text-foreground font-medium">Why 12ml? </span>
                                    This is the oil, not the spray. Fully concentrated, alcohol-free, one drop outlasts a handful of sprays. Small bottle, big reach, fits anywhere.
                                </p>
                            </div>
                        )}

                        {/* Scent notes — Top / Heart / Base, placed above the fold so
                            customers who don't scroll still see it. Falls back to the
                            flat notes field for products without the structured ones. */}
                        {[
                            { label: 'Top', notes: product.topNotes },
                            { label: 'Heart', notes: product.heartNotes },
                            { label: 'Base', notes: product.baseNotes },
                        ].some(tier => tier.notes?.length) ? (
                            <div className="bg-card rounded-2xl p-6 border border-border mb-4">
                                <h3 className="text-foreground font-medium tracking-wide mb-4">Scent Notes</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {[
                                        { label: 'Top', notes: product.topNotes },
                                        { label: 'Heart', notes: product.heartNotes },
                                        { label: 'Base', notes: product.baseNotes },
                                    ].filter(tier => tier.notes?.length).map((tier) => (
                                        <div key={tier.label} className="sm:border-l sm:first:border-l-0 sm:border-border sm:pl-4 sm:first:pl-0">
                                            <p className="text-[#CEAC8F] text-[10px] font-medium tracking-widest uppercase mb-2">{tier.label}</p>
                                            <p className="text-muted-foreground text-sm leading-relaxed">{tier.notes.join(', ')}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : cleanNotes(product.notes) && (
                            <div className="bg-card rounded-2xl p-6 border border-border mb-4">
                                <h3 className="text-foreground font-medium tracking-wide mb-3">Scent Notes</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    {cleanNotes(product.notes)}
                                </p>
                            </div>
                        )}

                        {/* How to Apply — same copy for every product, kept short and
                            placed next to the notes so it's visible without scrolling. */}
                        <div className="flex items-start gap-3 text-sm text-muted-foreground bg-card rounded-2xl p-6 border border-border mb-8">
                            <Droplet className="w-5 h-5 text-[#CEAC8F] flex-shrink-0 mt-0.5" />
                            <p className="leading-relaxed">
                                <span className="text-foreground font-medium">How to Apply — </span>
                                Dab a small amount onto pulse points — wrists, neck, and inner elbows. A little goes a long way.
                            </p>
                        </div>

                        {/* Description — renders HTML from Shopify preserving paragraph spacing */}
                        {product.descriptionHtml ? (
                            <div
                                className="text-muted-foreground leading-relaxed mb-8 product-description"
                                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                            />
                        ) : (
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                {product.description}
                            </p>
                        )}

                        {/* Buttons */}
                        <div ref={buttonsRef} className="flex flex-col sm:flex-row gap-3 mb-8">
                            <button
                                onClick={handleAddToCart}
                                disabled={!product.availableForSale || cartLoading}
                                className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-lg font-medium transition-all ${
                                    added
                                        ? 'bg-green-600 text-white'
                                        : product.availableForSale
                                            ? 'bg-[#CEAC8F] text-black hover:bg-[#d4b896]'
                                            : 'bg-muted text-muted-foreground cursor-not-allowed'
                                }`}
                            >
                                {added ? (
                                    <><Check className="w-5 h-5" /> Added to Cart</>
                                ) : (
                                    <><ShoppingBag className="w-5 h-5" /> {product.availableForSale ? 'Add to Cart' : 'Sold Out'}</>
                                )}
                            </button>

                            {product.availableForSale && (
                                <button
                                    onClick={handleBuyNow}
                                    disabled={cartLoading}
                                    className="flex-1 py-4 rounded-lg font-medium border border-[#CEAC8F] text-[#CEAC8F] hover:bg-[#CEAC8F] hover:text-black transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    Buy Now
                                </button>
                            )}

                            <div className="relative">
                                {wishlistTooltip && (
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-background text-foreground text-xs px-3 py-1.5 rounded-lg border border-border whitespace-nowrap shadow-lg z-10">
                                        Sign in to save
                                    </div>
                                )}
                                <button
                                    onClick={handleWishlist}
                                    className="h-full min-h-[56px] px-5 rounded-lg border border-border hover:border-[#CEAC8F]/50 flex items-center justify-center transition-all hover:scale-105"
                                    aria-label="Save to wishlist"
                                >
                                    <Heart
                                        className={`w-5 h-5 transition-colors ${
                                            isWishlisted(product.handle)
                                                ? 'fill-[#CEAC8F] text-[#CEAC8F]'
                                                : 'text-muted-foreground'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Trust badges */}
                        <div className="space-y-3 border-t border-border pt-6">
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <Truck className="w-5 h-5 text-[#CEAC8F] flex-shrink-0" />
                                <span>Free standard delivery on all orders. Express delivery €9.99.</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <RefreshCw className="w-5 h-5 text-[#CEAC8F] flex-shrink-0" />
                                <span>14-day returns. Item must be unused and in original packaging.</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <Shield className="w-5 h-5 text-[#CEAC8F] flex-shrink-0" />
                                <span>Secure checkout powered by Shopify.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recently Viewed */}
                <RecentlyViewed currentHandle={product.handle} />

                {/* You Might Also Like */}
                {related.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-3xl font-light text-foreground tracking-wide">You Might Also Like</h2>
                            <div className="flex gap-2">
                                <button onClick={scrollLeft} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-[#CEAC8F] hover:border-[#CEAC8F] transition-all">
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button onClick={scrollRight} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-[#CEAC8F] hover:border-[#CEAC8F] transition-all">
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div
                            ref={sliderRef}
                            className="flex gap-6 overflow-x-auto pb-4"
                            style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {related.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/product/${item.handle}`}
                                    className="flex-shrink-0 w-64 group"
                                    style={{ scrollSnapAlign: 'start' }}
                                >
                                    <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-[#CEAC8F]/30 transition-all duration-500">
                                        <div className="relative aspect-[3/4] overflow-hidden">
                                            {NEW_RELEASE_HANDLES.includes(item.handle) && (
                                                <span className="absolute top-3 left-3 z-10 bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                    New
                                                </span>
                                            )}
                                            <img
                                                src={item.image}
                                                alt={item.imageAlt}
                                                loading="lazy"
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <h3 className="text-foreground font-light group-hover:text-[#CEAC8F] transition-colors mb-1">
                                                {item.name}
                                            </h3>
                                            <ProductRatingBadge productId={item.id} productTitle={item.name} />
                                            {item.handle !== GIFT_BAG_HANDLE && (
                                                <p className="text-muted-foreground/70 text-[10px] tracking-widest mb-1 uppercase">12ml</p>
                                            )}
                                            <p className="text-[#CEAC8F]">{symbol}{item.price.toFixed(2)}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Reviews — productId is the numeric Shopify product ID (the tail
                    segment of the Storefront API's GID), matching the ID Shopify's
                    Admin API uses on order line items for review verification. */}
                <ProductReviews productId={product.id?.split('/').pop()} handle={product.handle} productName={product.name} />
            </div>

            {/* Mobile sticky Add to Cart — shares the main button's handler and
                state, so confirmation, sold-out and tracking behave the same. */}
            <StickyAddToCart
                product={product}
                symbol={symbol}
                added={added}
                disabled={cartLoading}
                onAdd={handleAddToCart}
                targetRef={buttonsRef}
            />
        </div>
    );
};

export default ProductDetail;